import { AxiosError } from 'axios';
import { ServerResponse, WithAuditDetails } from 'data/types';
import { History } from 'history';
import { Dispatch, Action } from 'redux';
import { RootState } from 'data/root.reducers';
import i18n from 'i18n';
import { showSuccessMessage, showError } from 'shared/toaster';
import { ThunkDispatch } from 'redux-thunk';
import { extractErrorMessage } from '../../utils';
import * as api from './afWhitelist.api';
import { getSelectedAfWhitelist } from './afWhitelist.selectors';
import {
  GET_AFWHITELIST_LIST,
  GET_AFWHITELIST,
  CREATE_AFWHITELIST,
  UPDATE_AFWHITELIST,
  DELETE_AFWHITELIST,
  GO_TO_AFWHITELIST_LIST,
  GO_TO_AFWHITELIST_EDIT,
  GO_TO_AFWHITELIST_VIEW,
  GO_TO_AFWHITELIST_CREATE,
  SELECT_AFWHITELIST,
  DeleteAfWhitelistThunkAction,
  UpdateAfWhitelistThunkAction,
  CreateAfWhitelistThunkAction,
  SET_AFWHITELIST_LOADING,
  SetAfWhitelistLoadingAction,
  AfWhitelistActionTypes,
  AfWhitelist,
  SelectAfWhitelistAction,
} from './afWhitelist.types';
import { AFWHITELIST, SYSTEM_CONFIGURATION_ROUTE, SYSTEM } from '../../constants';

export function goToAfWhitelistList(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_AFWHITELIST_LIST,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${SYSTEM}${AFWHITELIST}`);
  };
}

export function goToAfWhitelistCreate(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_AFWHITELIST_CREATE,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${SYSTEM}${AFWHITELIST}/create`);
  };
}

export function goToAfWhitelistEdit(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_AFWHITELIST_EDIT,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${SYSTEM}${AFWHITELIST}/edit`);
  };
}

export function goToAfWhitelistView(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_AFWHITELIST_VIEW,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${SYSTEM}${AFWHITELIST}/view`);
  };
}

export function setAfWhitelistLoadingAction(
  isLoading: boolean,
): SetAfWhitelistLoadingAction {
  return {
    type: SET_AFWHITELIST_LOADING,
    payload: { isLoading },
  };
}

export function getAfWhitelistList(): AfWhitelistActionTypes {
  return {
    type: GET_AFWHITELIST_LIST,
    payload: api.getAfWhitelistList(),
  };
}

export function getAfWhitelist(
  id: string,
): AfWhitelistActionTypes {
  return {
    type: GET_AFWHITELIST,
    payload: api.getAfWhitelist(id),
  };
}

export function createAfWhitelist(
  body: WithAuditDetails<AfWhitelist>,
  history: History,
): CreateAfWhitelistThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    try {
      await dispatch({
        type: CREATE_AFWHITELIST,
        payload: api.createAfWhitelist(body),
      });

      dispatch(getAfWhitelistList());
      dispatch(goToAfWhitelistList(history));
      showSuccessMessage(i18n.t('afWhitelist.create.success', { name: body.data?.AFID }), 5000);

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('afWhitelist.create.failed', { name: body.data?.AFID, error: errorMessage }));
    }
  };
}

export function createAfWhitelistFromForm(
  body: WithAuditDetails<AfWhitelist>,
): CreateAfWhitelistThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    dispatch(setAfWhitelistLoadingAction(true));
    try {
      await dispatch({
        type: CREATE_AFWHITELIST,
        payload: api.createAfWhitelist(body),
      });

      showSuccessMessage(i18n.t('afWhitelist.create.success', { name: body.data?.AFID }), 5000);

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('afWhitelist.create.failed', { name: body.data?.AFID, error: errorMessage }));

    } finally {
      dispatch(setAfWhitelistLoadingAction(false));
      dispatch(getAfWhitelistList());
    }
  };
}

export function updateAfWhitelist(
  id: string,
  body: WithAuditDetails<AfWhitelist>,
  history: History,
): UpdateAfWhitelistThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    try {
      await dispatch({
        type: UPDATE_AFWHITELIST,
        payload: api.updateAfWhitelist(id, body),
      });

      dispatch(getAfWhitelistList());
      dispatch(goToAfWhitelistList(history));
      showSuccessMessage(i18n.t('afWhitelist.update.success', { name: body.data?.AFID }), 5000);

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('afWhitelist.update.failed', { name: body.data?.AFID, error: errorMessage }));
    }
  };
}

export function deleteAfWhitelist(
  id: string,
  body: WithAuditDetails<void>,
  history?: History,
): DeleteAfWhitelistThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>, getState: () => RootState) => {
    const [selectedAfWhitelist] = getSelectedAfWhitelist(getState());

    try {
      await dispatch({
        type: DELETE_AFWHITELIST,
        payload: api.deleteAfWhitelist(id, body),
      });

      showSuccessMessage(i18n.t('afWhitelist.delete.success', { name: selectedAfWhitelist?.AFID }), 5000);
      if (history != null) {
        dispatch(goToAfWhitelistList(history));
      }
    } catch (e) {
      showError(i18n.t('afWhitelist.delete.failed', { name: selectedAfWhitelist?.AFID }));

    } finally {
      dispatch(getAfWhitelistList());
    }
  };
}

export function selectAfWhitelist(
  afWhitelist: AfWhitelist[],
): SelectAfWhitelistAction {
  return {
    type: SELECT_AFWHITELIST,
    payload: afWhitelist,
  };
}
